import { unzipSync } from 'fflate'
import headerTemplate from './header.html?raw'
import emptyTemplate from './empty.html?raw'
import markdownTemplate from './markdown.html?raw'
import type { AxiosError, AxiosInstance } from 'axios'
import { defu } from 'defu'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { extractCSSRules } from '~/lib/services/export/styles'
import { openPopup } from '~/helper/popup'
import { DeepRequired } from '~/types/helpers'
import { string2blob, string2file, triggerDownloadBlob } from '~/helper/download'
import { GotenbergIgnoreClass, NUXT_PROXY_GOTENBERG_PREFIX } from '~/lib/constants'
import { logDetailed } from '~/helper/log'
import mime from 'mime'
import { joinURL } from 'ufo'
import useI18nGlobal from '~/composables/common/useI18nGlobal'
import {
  embedFontsAsBase64,
  embedImagesAsBase64,
  extractFontUrls,
  shouldImageBeSkipped,
} from '~/lib/services/export/assetsProcessor'
import { asCached } from '~/helper'
import chalk from 'chalk'
import { isFile } from 'is-what'
import slugify from '@sindresorhus/slugify'
import { DebugLabelClassName } from '~/composables/DebugComponentNamesMixin'

export enum PageSizePreset {
  A0 = 'A0',
  A1 = 'A1',
  A2 = 'A2',
  A3 = 'A3',
  A4 = 'A4',
  A5 = 'A5',
  A6 = 'A6',
}

export const PAGE_SIZE_DIMENSIONS: Record<PageSizePreset, PageSize> = {
  [PageSizePreset.A0]: { width: 33.1, height: 46.8 },
  [PageSizePreset.A1]: { width: 23.4, height: 33.1 },
  [PageSizePreset.A2]: { width: 16.54, height: 23.4 },
  [PageSizePreset.A3]: { width: 11.7, height: 16.54 },
  [PageSizePreset.A4]: { width: 8.27, height: 11.7 },
  [PageSizePreset.A5]: { width: 5.83, height: 8.27 },
  [PageSizePreset.A6]: { width: 4.13, height: 5.83 },
}

export type PageSize = {
  width: number | `${string}px` // Paper width, in inches (default 8.5)
  height: number | `${string}px` //Paper height, in inches (default 11)
}
export type PageMargins = {
  top?: number // Top margin, in inches (default 0.39)
  bottom?: number // Bottom margin, in inches (default 0.39)
  left?: number // Left margin, in inches (default 0.39)
  right?: number // Right margin, in inches (default 0.39)
}
export type EmulatedMediaType = 'screen' | 'print'
export type GotenbergOptions = {
  singlePage?: boolean // Print the entire content in one single page (default false)
  size?: PageSize | PageSizePreset
  margins?: PageMargins
  // waitDelay?: `${number}s`

  preferCssPageSize?: boolean // Define whether to prefer page size as defined by CSS (default false)
  printBackground?: boolean // Print the background graphics (default false)
  omitBackground?: boolean // Hide the default white background and allow generating PDFs with transparency (default false)
  landscape?: boolean // Set the paper orientation to landscape (default false)
  scale?: number // The scale of the page rendering (default 1.0)
  emulatedMediaType?: EmulatedMediaType
  waitForNetworkIdle?: boolean
  nativePageRanges?: { from: number; to: number } | '' // Page ranges to print  - empty means all pages.
}

type GotenbergSplitPdfOptions = {
  splitMode: 'intervals' | 'pages' // Either intervals or pages.
  splitSpan: number // Either the intervals or the page ranges to extract, depending on the selected mode.
  splitUnify: boolean // Specify whether to put extracted pages into a single file or as many files as there are page ranges. Only works with pages mode.
  metadata: Record<any, any> // The metadata to write (JSON format).
}
export type PDFFilename = `${string}.pdf`
type GotenbergExportOptions = GotenbergOptions & {
  filename: PDFFilename
  popup?: boolean
  download?: boolean
  ignoreElements?: (el: Element) => boolean
  base64EmbedImages?: boolean
  base64EmbedFonts?: boolean
}

function defaultIgnoreElements(el: Element) {
  return el.classList.contains(GotenbergIgnoreClass) || el.classList.contains(DebugLabelClassName)
}

function removeIgnoredElements(body: HTMLElement, ignoreElements: (el: Element) => boolean) {
  if (ignoreElements(body)) {
    body.replaceChildren()
    return
  }

  const treeWalker = document.createTreeWalker(body, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      return ignoreElements(node as Element) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    },
  })

  // Collect elements first to not remove nodes which are actively traversed
  const elementsToRemove: Element[] = []
  let currentNode: Node | null = treeWalker.currentNode
  while (currentNode) {
    elementsToRemove.push(currentNode as Element)
    currentNode = treeWalker.nextNode()
  }

  for (const el of elementsToRemove) {
    el.remove()
  }
}

/**
 * Prepares HTML for Gotenberg by creating a clean copy of the element
 * with applied styles and removed ignored elements
 * Now async to support font embedding
 */
async function prepareHTMLDocument(bodyElement: HTMLElement, options?: GotenbergExportOptions): Promise<Document> {
  // create a necessary body element
  let body = bodyElement
  if (body.nodeName.toLowerCase() !== 'body') {
    body = document.createElement('body')
    body.appendChild(bodyElement)
  }

  removeIgnoredElements(body, (el) => options?.ignoreElements?.(el) || defaultIgnoreElements(el))

  // Extract all styles
  const css = await extractCSSRules()

  // Create a complete HTML document with the element and styles
  const doc = createDocument({
    css,
    body,
  })

  doc.title = options?.filename || 'PDF-Export'

  return doc
}

function prepareHeaderHTML() {
  return string2blob(headerTemplate, 'text/html')
}

function prepareEmptyHTML(): Blob {
  return string2blob(emptyTemplate, 'text/html')
}

/**
 * Helper to create an HTML Document
 */
function createDocument({ css, body }: { css: HTMLStyleElement; body: HTMLElement }): Document {
  const doc = document.implementation.createHTMLDocument()

  // body - replaceWith() is the most robust in both happy-dom and browser
  doc.body.replaceWith(body)

  // The extracted app CSS carries no body reset - without this, Chromium's
  // default 8px body margin pushes the 100vh page containers past every page
  // boundary, appending a blank trailing page to each document
  // const baseStyle = doc.createElement('style')
  // baseStyle.textContent = 'body { margin: 0 }'
  // doc.head.appendChild(baseStyle)

  // Style
  doc.head.appendChild(css)

  // Important UTF Meta
  const meta = doc.createElement('meta')
  meta.setAttribute('charset', 'utf-8')
  doc.head.appendChild(meta)

  return doc
}

/**
 * Download an HTML element as a PDF using the Gotenberg service
 */
type PDFParts = {
  body: File
  header?: File
  footer?: File
  images?: File[]
  fonts?: File[]
}

function isMarkdown(filename: string) {
  return filename.endsWith('.md') || filename.endsWith('.markdown')
}

function resolveGotenbergAssetURL(url: string): string | null {
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return null
  }

  try {
    const resolvedUrl = new URL(url, document.location.href)
    return resolvedUrl.origin === document.location.origin ? resolvedUrl.href : null
  } catch {
    return null
  }
}

/**
 * Transform an HTML file into PDF
 *
 * See: https://gotenberg.dev/docs/routes#html-file-into-pdf-route
 */
export class GotenbergService {
  private http: AxiosInstance

  ROUTE_FLATTEN: string
  ROUTE_PDF_SPLIT: string
  ROUTE_PDF_MERGE: string
  ROUTE_CONVERT_HTML: string
  ROUTE_CONVERT_MARKDOWN: string

  DEBUG_EXPORT_FILES: Ref<boolean> = ref(false)
  DEBUG_SPLIT_PDF_FILES: Ref<boolean> = ref(false)
  DEBUG_GENERATE_PDF: Ref<boolean> = ref(false)

  fontCache: ReturnType<typeof asCached<string, Promise<Blob>>>
  imageCache: ReturnType<typeof asCached<string, Promise<Blob>>>

  constructor(http: AxiosInstance, baseUrl: string = NUXT_PROXY_GOTENBERG_PREFIX) {
    this.http = http

    this.ROUTE_FLATTEN = joinURL(baseUrl, '/forms/pdfengines/flatten')
    this.ROUTE_PDF_SPLIT = joinURL(baseUrl, '/forms/pdfengines/split')
    this.ROUTE_PDF_MERGE = joinURL(baseUrl, '/forms/pdfengines/merge')
    this.ROUTE_CONVERT_HTML = joinURL(baseUrl, '/forms/chromium/convert/html')
    this.ROUTE_CONVERT_MARKDOWN = joinURL(baseUrl, '/forms/chromium/convert/markdown')

    this.fontCache = asCached(this._fetchUrlAsBlob.bind(this))
    this.imageCache = asCached(this._fetchUrlAsBlob.bind(this))
  }

  /**
   * SRC of <img>-tags need to be "top-level" and be POSTed as File-Object to Gotenberg.
   * 1. Download src tag to `File`
   * 2. Replace the src tag with the top level name
   *
   * See: https://gotenberg.dev/docs/convert-with-chromium/convert-html-to-pdf
   */
  async prepareGotenbergObjectURLImages(body: HTMLElement): Promise<File[]> {
    const images: File[] = []
    const imgElements: HTMLImageElement[] = Array.from(body.querySelectorAll('img'))
    // Skip cross-origin images like the font path does: they cannot be fetched
    // from the app origin and would otherwise fail the whole export
    const imgElementsValid = imgElements.filter(
      (img) => !shouldImageBeSkipped(img) && resolveGotenbergAssetURL(img.src) !== null,
    )

    // Step 1: Map URLs to filenames (relative URLs?)
    const urlsMap: { [key: string]: HTMLImageElement[] } = {}
    for (const img of imgElementsValid) {
      urlsMap[img.src] ??= []
      urlsMap[img.src].push(img)
    }

    const imagesLog: string[] = []
    const mapping = Object.entries(urlsMap).map(async ([url, imgs], index) => {
      let blob: Blob
      try {
        blob = await this.imageCache.get(url)
      } catch (error) {
        // Skip the image instead of failing the whole export
        // (embedImagesAsBase64 handles failures the same way)
        console.warn(`Failed to fetch image for export: ${url}`, error)
        return
      }
      const ext: string = mime.getExtension(blob.type) || 'png'
      const filename = `image_${index}_${slugify(url)}.${ext}`

      const file = new File([blob], filename, { type: blob.type })
      images.push(file)
      for (const img of imgs) {
        imagesLog.push(`${img.src} -> ${filename}`)
        img.src = filename
      }
    })
    await Promise.all(mapping)

    logDetailed(`Processed ${images.length} images and replaced ${imagesLog.length} URLs`, {
      body: imagesLog.join('\n'),
    })

    return images
  }

  async _fetchUrlAsBlob(url: string) {
    const response = await this.http.get<Blob>(url, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async prepareGotenbergObjectURLFonts(doc: Document, fonts: (string | RegExp)[] = [/.*/]): Promise<File[]> {
    const style = doc.head.querySelector('style')
    const cssText = style?.textContent

    if (!style || !cssText) {
      return []
    }

    const urlsMap: Record<string, string[]> = {}

    const fontUrls = Array.from(new Set(extractFontUrls(cssText))).filter((fontUrl) =>
      fonts.some((font) => fontUrl.match(font)),
    )

    for (const originalUrl of fontUrls) {
      const resolvedUrl = resolveGotenbergAssetURL(originalUrl)
      if (!resolvedUrl) {
        continue
      }

      urlsMap[resolvedUrl] ??= []
      urlsMap[resolvedUrl].push(originalUrl)
    }

    let processedCSS = cssText
    const fontsLog: string[] = []
    const preparedFonts = await Promise.all(
      Object.entries(urlsMap).map(async ([url, cssUrls], index) => {
        const blob = await this.fontCache.get(url)
        const ext = mime.getExtension(blob.type) || url.split(/[?#]/)[0].match(/\.([^.]+)$/)?.[1] || 'woff2'
        const type = blob.type || mime.getType(ext) || 'font/woff2'
        const filename = new URL(url).pathname.split('/').reverse()[0]
        // const filename = `font_${index}_${slugify(url)}.${ext}`

        return {
          cssUrls,
          file: new File([blob], filename, { type }),
          filename,
          sourceUrl: url,
        }
      }),
    )

    for (const preparedFont of preparedFonts) {
      for (const cssUrl of preparedFont.cssUrls) {
        const escapedUrl = cssUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        processedCSS = processedCSS.replace(
          new RegExp(`url\\(\\s*["']?${escapedUrl}["']?\\s*\\)`, 'gi'),
          `url("${preparedFont.filename}")`,
        )
        fontsLog.push(`${preparedFont.sourceUrl} -> ${preparedFont.filename}`)
      }
    }

    style.textContent = processedCSS

    logDetailed(`Processed ${preparedFonts.length} fonts and replaced ${fontsLog.length} URLs`, {
      body: fontsLog.join('\n'),
    })

    return preparedFonts.map((preparedFont) => preparedFont.file)
  }

  async prepareAsFile(element: HTMLElement, options?: GotenbergExportOptions) {
    // 1. Clone the element to avoid modifying the original DOM & create Document
    const elementClone = element.cloneNode(true) as HTMLElement
    const document = await prepareHTMLDocument(elementClone, options)

    // 2. Embed or extract image assets
    const images: File[] = []
    if (options?.base64EmbedImages) {
      console.info(chalk.dim('Embedding images..'))
      await embedImagesAsBase64(document.body)
    } else {
      console.info(chalk.dim('POSTing images..'))
      images.push(...(await this.prepareGotenbergObjectURLImages(document.body)))
    }

    // 3. Embed or extract font assets
    const fonts: File[] = []
    const targetedFonts = [
      // /.*/,
      'materialdesignicons',
      'montserrat',
    ]
    if (options?.base64EmbedFonts) {
      console.info(chalk.dim('Embedding fonts..'))
      await embedFontsAsBase64(document, targetedFonts)
    } else {
      console.info(chalk.dim('POSTing fonts..'))
      fonts.push(...(await this.prepareGotenbergObjectURLFonts(document, targetedFonts)))
    }

    const body = document.documentElement.outerHTML
    const file = string2file(body, 'body.html', 'text/html')

    return {
      file,
      images: images,
      fonts: fonts,
    }
  }

  /**
   * Download an HTML element as a PDF using the Gotenberg service
   */
  async element2pdf(element: HTMLElement, options: GotenbergExportOptions): Promise<File> {
    const _options: GotenbergExportOptions = defu(options, {
      popup: false,
      base64EmbedFonts: false,
      base64EmbedImages: false,
    } satisfies Partial<GotenbergExportOptions>)

    const { file, images, fonts } = await this.prepareAsFile(element, _options)
    const pdf = await this.generatePDF(_options.filename, { body: file, images, fonts }, _options)

    // Debugging results
    if (this.DEBUG_EXPORT_FILES.value) {
      triggerDownloadBlob(file, _options.filename.replace('.pdf', '.html'))
      triggerDownloadBlob(pdf, _options.filename)
    }

    if (_options.popup) {
      openPopup(pdf, pdf.name)
      return pdf
    }
    if (_options.download && !this.DEBUG_EXPORT_FILES.value) {
      triggerDownloadBlob(pdf, _options.filename)
    }
    return pdf
  }

  /**
   * Flatten means "persisting" annotations and forms to the PDF
   *
   * See: https://gotenberg.dev/docs/manipulate-pdfs/flatten-pdfs
   */
  async flattenPDF(pdf: File): Promise<File> {
    const formData = new FormData()
    formData.append('files', pdf, pdf.name)

    // Send request to Gotenberg
    const response = await this.http.post(this.ROUTE_FLATTEN, formData, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return new File([response.data], pdf.name)
  }

  /**
   * Merges multiple PDF files into a single PDF, keeping the input order.
   * Gotenberg combines the files in alphanumeric order of their names, so a
   * zero-padded index is prepended - this also keeps duplicate names unique.
   *
   * See: https://gotenberg.dev/docs/manipulate-pdfs/merge-pdfs
   */
  async mergePDFs(pdfs: File[], filename: PDFFilename): Promise<File> {
    const formData = new FormData()
    const padLength = String(pdfs.length - 1).length
    for (const [index, pdf] of pdfs.entries()) {
      formData.append('files', pdf, `${String(index).padStart(padLength, '0')}_${pdf.name}`)
    }

    // Send request to Gotenberg
    const response = await this.http.post(this.ROUTE_PDF_MERGE, formData, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return new File([response.data], filename)
  }

  /**
   * Splits a PDF using Gotenberg and returns an array of PDF blobs.
   * If splitUnify is false, expects a .zip file and extracts all PDFs.
   * If splitUnify is true, expects a single .pdf file.
   *
   * See: https://gotenberg.dev/docs/routes#split-pdfs-route
   */
  async splitPDF(pdf: File, options?: GotenbergSplitPdfOptions): Promise<File[]> {
    const _options: Required<GotenbergSplitPdfOptions> = defu(options, {
      splitMode: 'intervals',
      splitSpan: 1,
      metadata: {},
      splitUnify: false,
    })
    const formData = new FormData()
    formData.append('files', pdf, pdf.name)
    formData.append('splitMode', _options.splitMode)
    formData.append('splitSpan', _options.splitSpan.toString())
    formData.append('metadata', JSON.stringify(_options.metadata))
    formData.append('splitUnify', _options.splitUnify.toString())

    // Send request to Gotenberg
    const response = await this.http.post(this.ROUTE_PDF_SPLIT, formData, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    const contentType = response.headers?.['content-type'] || response.data?.type || ''

    const singlePDF = (): File[] => {
      if (this.DEBUG_SPLIT_PDF_FILES.value) {
        triggerDownloadBlob(response.data, 'split.zip')
      }
      return [new File([response.data], pdf.name)]
    }
    const zippedPDFs = (files: ReturnType<typeof unzipSync>): File[] => {
      const pdfs: File[] = []
      const inOrder = Object.entries(files).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      for (const [name, data] of inOrder) {
        if (name.endsWith('.pdf')) {
          pdfs.push(new File([data as BlobPart], name))
        }
      }

      if (this.DEBUG_SPLIT_PDF_FILES.value) {
        pdfs.map((f) => triggerDownloadBlob(f, f.name))
      }

      return pdfs
    }

    // Singe PDF
    if (contentType.includes('pdf')) {
      return singlePDF()
    }

    const arrayBuffer = await response.data.arrayBuffer()
    const u8 = new Uint8Array(arrayBuffer)

    // ZIP File
    if (contentType.includes('zip')) {
      return zippedPDFs(unzipSync(u8))
    }

    // Fallback: try to guess by magic bytes if content-type is missing
    // (must run before unzipSync, which throws on non-ZIP data)
    // PDF magic: %PDF
    if (_options.splitUnify && u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46) {
      return singlePDF()
    }
    // ZIP magic: PK
    if (!_options.splitUnify && u8[0] === 0x50 && u8[1] === 0x4b) {
      return zippedPDFs(unzipSync(u8))
    }
    throw new Error('Unknown response type from Gotenberg: expected PDF or ZIP')
  }

  /**
   * Transform an HTML file into PDF
   *
   * See: https://gotenberg.dev/docs/routes#html-file-into-pdf-route
   */
  async generatePDF(filename: PDFFilename, parts: PDFParts, options?: GotenbergOptions): Promise<File> {
    const _options: DeepRequired<GotenbergOptions> = defu(options, {
      landscape: true,
      printBackground: true,
      scale: 1.0,

      size: PAGE_SIZE_DIMENSIONS.A4,
      margins: {
        top: 0.4,
        bottom: 0.4,
        left: 0.4,
        right: 0.4,
      },

      singlePage: false,
      emulatedMediaType: 'print',
      // false = Gotenberg's own default; no CSS in this app declares @page anyway
      preferCssPageSize: false,
      waitForNetworkIdle: true,
      omitBackground: false,
      nativePageRanges: '',
    } satisfies GotenbergOptions)

    // Create form data
    const formData = new FormData()

    let body = parts.body
    const header = parts.header ?? prepareEmptyHTML()
    const footer = parts.footer ?? prepareEmptyHTML()

    const height =
      typeof _options.size === 'string'
        ? PAGE_SIZE_DIMENSIONS[_options.size as PageSizePreset].height
        : _options.size.height
    const width =
      typeof _options.size === 'string'
        ? PAGE_SIZE_DIMENSIONS[_options.size as PageSizePreset].width
        : _options.size.width

    // Debugging
    // triggerDownloadBlob(body, 'debug.html')

    let url = this.ROUTE_CONVERT_HTML
    // Handle markdown
    if (isMarkdown(parts.body.name)) {
      const markdownFile = new File([parts.body], 'file.md')
      body = string2file(markdownTemplate, 'index.html', 'text/html')
      formData.append('files', markdownFile, 'file.md')
      url = this.ROUTE_CONVERT_MARKDOWN
    }

    // Files
    formData.append('files', body, 'index.html')
    formData.append('files', header, 'header.html')
    formData.append('files', footer, 'footer.html')
    const assets = [parts.images, parts.fonts].flat().filter(isFile)
    if (assets.length) {
      console.groupCollapsed(chalk.dim(`Appending ${assets.length} assets `))
      for (const asset of assets) {
        console.log(asset.name)
        formData.append('files', asset, asset.name)
      }
      console.groupEnd()
    }

    // Settings
    formData.append('landscape', _options.landscape.toString())
    formData.append('printBackground', _options.printBackground.toString())
    formData.append('scale', _options.scale.toString())

    // Page Dimensions
    formData.append('paperHeight', String(height))
    formData.append('paperWidth', String(width))

    formData.append('marginTop', _options.margins.top.toString())
    formData.append('marginBottom', _options.margins.bottom.toString())
    formData.append('marginLeft', _options.margins.left.toString())
    formData.append('marginRight', _options.margins.right.toString())

    // Other
    formData.append('singlePage', _options.singlePage.toString())
    formData.append('emulatedMediaType', _options.emulatedMediaType)
    formData.append('preferCssPageSize', _options.preferCssPageSize.toString())
    formData.append('omitBackground', _options.omitBackground.toString())
    // Gotenberg names this field inversely to our option
    formData.append('skipNetworkIdleEvent', (!_options.waitForNetworkIdle).toString())
    if (_options.nativePageRanges !== '') {
      formData.append('nativePageRanges', `${_options.nativePageRanges.from}-${_options.nativePageRanges.to}`)
    }

    if (this.DEBUG_GENERATE_PDF.value) {
      triggerDownloadBlob(body, body.name)
    }

    // Send request to Gotenberg
    const start = performance.now()
    let response
    try {
      response = await this.http.post(url, formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    } catch (e) {
      // Gotenberg returns a 503 when the timeout limit is reached (default 30s)
      if ((e as AxiosError).status === 503) {
        const { t } = useI18nGlobal()
        ;(e as AxiosError).message =
          t('ReportErrorGotenberg503', { seconds: Math.round((performance.now() - start) / 1000) }) +
          `: ${(e as AxiosError).message}`
      }
      throw e
    }

    if (this.DEBUG_GENERATE_PDF.value) {
      triggerDownloadBlob(response.data, filename)
    }

    return new File([response.data], filename)
  }
}
